//todo move out of this folder
import COLOR_THEMES from "../../../../../colorThemes";
import {getParallelPoints, getPointOnTheLine} from "../../../helpers/sqftHelper";
import sqftConfig from "../store/sqftConfig";
import sqftEventManager from "../store/sqftEventManager";
import {getMidPoint, getPointBetweenPoints, getPointDistance} from "../../../helpers/sqftHelper";
import workspace from "../core/workspace";

export default class RenderFigures {

  /**
   * Draws a rounded rectangle using the current state of the canvas.
   * If you omit the last three params, it will draw a rectangle
   * outline with a 5 pixel border radius
   * @param {CanvasRenderingContext2D} ctx
   * @param {Number} x The top left x coordinate
   * @param {Number} y The top left y coordinate
   * @param {Number} width The width of the rectangle
   * @param {Number} height The height of the rectangle
   * @param {Number} [radius = 5] The corner radius; It can also be an object
   *                 to specify different radii for corners
   * @param {Number} [radius.tl = 0] Top left
   * @param {Number} [radius.tr = 0] Top right
   * @param {Number} [radius.br = 0] Bottom right
   * @param {Number} [radius.bl = 0] Bottom left
   * @param {Boolean} [isFilled = false] Whether to fill the rectangle.
   * @param {Boolean} [stroke = true] Whether to stroke the rectangle.
   */
  static roundedRect(ctx, x, y, width, height, radius, isFilled, stroke) {
    if (typeof stroke === 'undefined') {
      stroke = true;
    }
    if (typeof radius === 'undefined') {
      radius = 5;
    }
    if (typeof radius === 'number') {
      radius = {tl: radius, tr: radius, br: radius, bl: radius};
    } else {
      let defaultRadius = {tl: 0, tr: 0, br: 0, bl: 0};
      for (let side in defaultRadius) {
        radius[side] = radius[side] || defaultRadius[side];
      }
    }
    ctx.beginPath();
    ctx.moveTo(x + radius.tl, y);
    ctx.lineTo(x + width - radius.tr, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius.tr);
    ctx.lineTo(x + width, y + height - radius.br);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius.br, y + height);
    ctx.lineTo(x + radius.bl, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius.bl);
    ctx.lineTo(x, y + radius.tl);
    ctx.quadraticCurveTo(x, y, x + radius.tl, y);
    ctx.closePath();
    if (isFilled) {
      ctx.fill();
    }
    if (stroke) {
      ctx.stroke();
    }
  }

  static renderOval(ctx, w, h, x, y = 0, fullOval = false) {
    let lx = x - w * .5;
    let rx = x + w * .5;
    let ty = y - h * .5;
    let by = y + h * .5;

    let magic = 0.551915024494;
    let xMagic = magic * w * 0.5;
    let yMagic = h * magic * 0.5;

    ctx.moveTo(x, ty);
    ctx.bezierCurveTo(x + xMagic, ty, rx, y - yMagic, rx, y);
    ctx.bezierCurveTo(rx, y + yMagic, x + xMagic, by, x, by);
    if (fullOval) {
      ctx.bezierCurveTo(y - xMagic, by, lx, y + yMagic, lx, y);
      ctx.bezierCurveTo(lx, x - yMagic, x - xMagic, ty, x, ty);
    }
  }

  static drawArrow = (context, fromX, fromY, toX, toY) => {
    let headLen = 10, // length of head in pixels
      dx = toX - fromX,
      dy = toY - fromY,
      angle = Math.atan2(dy, dx);

    context.moveTo(fromX, fromY);
    context.lineTo(toX, toY);
    context.lineTo(toX - headLen * Math.cos(angle - Math.PI / 6), toY - headLen * Math.sin(angle - Math.PI / 6));
    context.moveTo(toX, toY);
    context.lineTo(toX - headLen * Math.cos(angle + Math.PI / 6), toY - headLen * Math.sin(angle + Math.PI / 6));

    context.moveTo(fromX, fromY);
    context.lineTo(fromX + headLen * Math.cos(angle - Math.PI / 6), fromY + headLen * Math.sin(angle - Math.PI / 6));
    context.moveTo(fromX, fromY);
    context.lineTo(fromX + headLen * Math.cos(angle + Math.PI / 6), fromY + headLen * Math.sin(angle + Math.PI / 6));
  };

  static drawRotatedArrow = (context, fromX, fromY, toX, toY, cornerPointX, cornerPointY, rotateAngle) => {
    const midpoint = getMidPoint({x: fromX, y: fromY}, {x: toX, y: toY});
    const rotateDirectionPoint = getPointBetweenPoints(midpoint, {x: cornerPointX, y: cornerPointY}, rotateAngle);
    let headLen = 7, // length of head in pixels
      dx = toX - rotateDirectionPoint.x,
      dy = toY - rotateDirectionPoint.y,
      angle = Math.atan2(dy, dx);
    const radius = getPointDistance({x: fromX, y: fromY}, {x: cornerPointX, y: cornerPointY});
    context.moveTo(fromX, fromY);
    context.arcTo(
      cornerPointX,
      cornerPointY,
      toX,
      toY,
      radius
    );

    //First arrow
    context.moveTo(toX, toY);
    context.lineTo(toX - headLen * Math.cos(angle - Math.PI / 6), toY - headLen * Math.sin(angle - Math.PI / 6));
    context.moveTo(toX, toY);
    context.lineTo(toX - headLen * Math.cos(angle + Math.PI / 6), toY - headLen * Math.sin(angle + Math.PI / 6));

    //Second arrow
    dx = rotateDirectionPoint.x - fromX;
    dy = rotateDirectionPoint.y - fromY;
    angle = Math.atan2(dy, dx);
    context.moveTo(fromX, fromY);
    context.lineTo(fromX + headLen * Math.cos(angle - Math.PI / 6), fromY + headLen * Math.sin(angle - Math.PI / 6));
    context.moveTo(fromX, fromY);
    context.lineTo(fromX + headLen * Math.cos(angle + Math.PI / 6), fromY + headLen * Math.sin(angle + Math.PI / 6));
  };

  static renderDoubleCircle(ctx, pointField, radius, lineWidth) {
    //outside circle
    ctx.arc(
      pointField.x,
      pointField.y,
      radius,
      0,
      Math.PI * 2,
      false
    );
    ctx.strokeStyle = COLOR_THEMES[sqftConfig.activeTheme].primary;
    ctx.fillStyle = '#ffffff';
    ctx.lineWidth = lineWidth;
    ctx.stroke();
    ctx.fill();

    //inside circle
    ctx.beginPath();
    ctx.arc(
      pointField.x,
      pointField.y,
      radius - 3,
      0,
      Math.PI * 2,
      false
    );

    ctx.fillStyle = COLOR_THEMES[sqftConfig.activeTheme].primary;
    ctx.lineWidth = lineWidth;
    ctx.fill();
  }

  static renderCircle(pointField,
                      radius = 1,
                      lineWidth = 1,
                      strokeStyle = '#000000',
                      fillStyle = null,
                      ctx = sqftEventManager.getCtx()
  ) {
    ctx.save();
    ctx.beginPath();
    ctx.arc(
      pointField.x,
      pointField.y,
      radius,
      0,
      Math.PI * 2,
      false
    );
    ctx.strokeStyle = strokeStyle;
    ctx.lineWidth = lineWidth;
    ctx.stroke();
    if (fillStyle) {
      ctx.fillStyle = fillStyle;//'#ffffff';
      ctx.fill();
    }
    ctx.closePath();
    ctx.restore();
  }

  static renderPolygon = (points,
                          lineWidth = 2,
                          strokeStyle = 'rgba(0, 0, 0, 0.5)',
                          ctx = sqftEventManager.getCtx()
  ) => {
    const firstPoint = points[0];
    ctx.beginPath();
    ctx.moveTo(firstPoint.x, firstPoint.y);
    for (const point of points) {
      ctx.lineTo(point.x, point.y);
    }
    ctx.lineWidth = lineWidth;
    ctx.strokeStyle = strokeStyle;
    ctx.stroke();
    ctx.closePath();
  }

  static renderPolygonArray = (points,
                          lineWidth = 2,
                          strokeStyle = 'rgba(0, 0, 0, 0.5)',
                          ctx = sqftEventManager.getCtx()
  ) => {
    const firstPoint = points[0];
    ctx.beginPath();
    ctx.moveTo(firstPoint[0], firstPoint[1]);
    for (const point of points) {
      ctx.lineTo(point[0], point[1]);
    }
    ctx.lineTo(firstPoint[0], firstPoint[1]);
    ctx.lineWidth = lineWidth;
    ctx.strokeStyle = strokeStyle;
    ctx.stroke();
    ctx.closePath();
  }

  static drawArrowsAround4points = (ctx, points, offset = 10) => {
    points.forEach((point, index) => {
      const prevPoint = points[index === 0 ? points.length - 1 : index - 1];
      const nextPoint = points[
        index === points.length - 1 ? 0 : index + 1
        ];
      const afterNextPoint = points[
        index === points.length - 1
          ? 1
          : index === points.length - 2
          ? 0
          : index + 2
        ];
      const offsetPoint1 = getPointOnTheLine(point, prevPoint, offset);
      const offsetPoint2 = getPointOnTheLine(nextPoint, afterNextPoint, offset);
      const [midPoint, midPoint2] = getParallelPoints(point, nextPoint, - offset);

      ctx.beginPath();
      RenderFigures.drawArrow( ctx,
        offsetPoint1.x,
        offsetPoint1.y,
        offsetPoint2.x,
        offsetPoint2.y,
      );
      RenderFigures.drawArrow( ctx,
        midPoint.x,
        midPoint.y,
        midPoint2.x,
        midPoint2.y,
      );
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.closePath();
    });
  };

  static drawArrowsAroundPoints = (ctx, points, offset = 10, minArrowLength = 2) => {
    points.forEach((point, index) => {
      const nextPoint = points[
        index === points.length - 1 ? 0 : index + 1
        ];
      const pointDistance = getPointDistance(point, nextPoint);
      const scale = workspace.getScale();

      if (pointDistance / scale > minArrowLength) {
        const [midPoint, midPoint2] = getParallelPoints(point, nextPoint, - offset);
        ctx.beginPath();
        RenderFigures.drawArrow( ctx,
          midPoint.x,
          midPoint.y,
          midPoint2.x,
          midPoint2.y,
        );
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.closePath();
      }
    });
  };

  static renderHandle = (ctx, midPoint, options) => {
    ctx.save();
    ctx.beginPath();
    ctx.fillStyle = options?.fillStyle ?? 'white';
    ctx.lineWidth = 2;
    ctx.strokeStyle = options?.strokeStyle ?? COLOR_THEMES[sqftConfig.activeTheme].primary;
    ctx.arc(midPoint.x, midPoint.y, 10, 0, 2 * Math.PI, false);
    ctx.fill();
    ctx.stroke();
    ctx.closePath();

    ctx.beginPath();
    ctx.moveTo(midPoint.x, midPoint.y);
    ctx.lineTo(midPoint.x + 5 , midPoint.y);
    ctx.lineTo(midPoint.x + 5 , midPoint.y + 3);

    ctx.moveTo(midPoint.x, midPoint.y);
    ctx.lineTo(midPoint.x - 5 , midPoint.y);
    ctx.lineTo(midPoint.x - 5 , midPoint.y + 3);
    ctx.stroke();

    ctx.restore();
  }

  static renderArrowInCircle(ctx, midPoint, lineFrom, lineTo, center) {
    // 1) compute edge vector
    const dx = lineTo.x - lineFrom.x;
    const dy = lineTo.y - lineFrom.y;

    // 2) get its outward normal (dx,dy) → (ny = dx, nx = -dy) or vice versa
    let nx = dy;
    let ny = -dx;

    // 3) decide if (nx,ny) points outward: normal·(midPoint‐center) should be >0
    const dot = nx * (midPoint.x - center.x) + ny * (midPoint.y - center.y);
    if (dot < 0) {
      // flip if it's pointing inward
      nx = -nx;
      ny = -ny;
    }

    // 4) compute angle of this normal
    const angle = Math.atan2(ny, nx);

    ctx.save();

    // Draw the outer circle
    ctx.beginPath();
    ctx.fillStyle   = 'white';
    ctx.lineWidth   = 2;
    ctx.strokeStyle = COLOR_THEMES[sqftConfig.activeTheme].primary;
    ctx.arc(midPoint.x, midPoint.y, 10, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.closePath();

    // prepare coordinate system for arrow
    ctx.translate(midPoint.x, midPoint.y);
    ctx.rotate(angle);

    // Draw a downward-pointing arrow
    // (actually will point along positive X in rotated coords)
    const shaftLength = 12;
    const headSize    = 6;

    ctx.beginPath();
    // shaft from –half to +half along X
    ctx.moveTo(-shaftLength/2, 0);
    ctx.lineTo( shaftLength/2, 0);

    // left wing of arrowhead
    ctx.moveTo( shaftLength/2, 0);
    ctx.lineTo(
      shaftLength/2 - headSize,
      -headSize/2
    );

    // right wing of arrowhead
    ctx.moveTo( shaftLength/2, 0);
    ctx.lineTo(
      shaftLength/2 - headSize,
      +headSize/2
    );

    ctx.stroke();
    ctx.restore();
  }

  /**
   * Draws a sink
   * @param {CanvasRenderingContext2D} ctx
   * @param {Number} x The top left x coordinate
   * @param {Number} y The top left y coordinate
   * @param {Number} width The width of the rectangle
   * @param {Number} height The height of the rectangle
   *                 to specify different radii for corners
   */
  static sink(ctx, x, y, width, height) {
    // Proportions of the original SVG
    const originalWidth = 340;
    const originalHeight = 463;
    const innerRectWidth = 284;
    const innerRectHeight = 410;
    const innerRectX = 29;
    const innerRectY = 29;
    const circleRadiusBig = 29;
    const circleRadiusSmall = 20.7142857;
    const circleCenterX = 142 + circleRadiusBig;
    const circleCenterY = 203 + circleRadiusBig;

    // Scaling proportions
    const scaleX = width / originalWidth;
    const scaleY = height / originalHeight;

    const lineWidth = 1;
    const lineColor = COLOR_THEMES[sqftConfig.activeTheme].primary; //"#4A90E2";
    ctx.lineWidth = lineWidth * scaleX;
    ctx.strokeStyle = lineColor;

    // Fill the background with white
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(x, y, width, height);

    // Drawing the outer rectangle
    ctx.beginPath();
    ctx.rect(x, y, width, height);
    ctx.stroke();

    // Drawing the inner rectangle
    const innerX = x + innerRectX * scaleX;
    const innerY = y + innerRectY * scaleY;
    const innerW = innerRectWidth * scaleX;
    const innerH = innerRectHeight * scaleY;
    ctx.beginPath();
    ctx.rect(innerX, innerY, innerW, innerH);
    ctx.stroke();

    // Drawing the circles
    const centerX = x + circleCenterX * scaleX;
    const centerY = y + circleCenterY * scaleY;
    const radiusBig = circleRadiusBig * scaleX;
    const radiusSmall = circleRadiusSmall * scaleX;

    ctx.beginPath();
    ctx.arc(centerX, centerY, radiusBig, 0, 2 * Math.PI);
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(centerX, centerY, radiusSmall, 0, 2 * Math.PI);
    ctx.stroke();
  }

  /**
   * Draws a doubleSink
   * @param {CanvasRenderingContext2D} ctx
   * @param {Number} x The top left x coordinate
   * @param {Number} y The top left y coordinate
   * @param {Number} width The width of the rectangle
   * @param {Number} height The height of the rectangle
   *                 to specify different radii for corners
   */
  static doubleSink(ctx, x, y, width, height) {
    // Proportions of the original doubleSink SVG
    const originalWidth = 341;
    const originalHeight = 511;
    const innerRectWidthTop = 282;
    const innerRectHeightTop = 153;
    const innerRectWidthBottom = 283;
    const innerRectHeightBottom = 285;
    const innerRectX = 29;
    const innerRectYTop = 26;
    const innerRectYBottom = 192;
    const circleRadiusBig = 29;
    const circleRadiusSmall = 20.7142857;
    const circleCenterX = 149; // Adjusted for double sink
    const circleCenterYTop = 103; // Top circle center
    const circleCenterYBottom = 335; // Bottom circle center

    // Scaling proportions based on provided width and height
    const scaleX = width / originalWidth;
    const scaleY = height / originalHeight;

    // Line thickness and color for doubleSink
    const lineWidth = 1.54 * scaleX; // Adjusted line width
    const lineColor = "#4A90E2";

    // Fill the background with white
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(x, y, width, height);

    // Draw the outer rectangle
    ctx.lineWidth = lineWidth;
    ctx.strokeStyle = lineColor;
    ctx.beginPath();
    ctx.rect(x, y, width, height);
    ctx.stroke();

    // Draw the top inner rectangle
    const innerX = x + innerRectX * scaleX;
    const innerYTop = y + innerRectYTop * scaleY;
    const innerWTop = innerRectWidthTop * scaleX;
    const innerHTop = innerRectHeightTop * scaleY;
    ctx.beginPath();
    ctx.rect(innerX, innerYTop, innerWTop, innerHTop);
    ctx.stroke();

    // Draw the bottom inner rectangle
    const innerYBottom = y + innerRectYBottom * scaleY;
    const innerWBottom = innerRectWidthBottom * scaleX;
    const innerHBottom = innerRectHeightBottom * scaleY;
    ctx.beginPath();
    ctx.rect(innerX, innerYBottom, innerWBottom, innerHBottom);
    ctx.stroke();

    // Draw top circles
    const centerXTop = x + circleCenterX * scaleX;
    const centerYTop = y + circleCenterYTop * scaleY;
    const radiusBig = circleRadiusBig * scaleX;
    const radiusSmall = circleRadiusSmall * scaleX;

    ctx.beginPath();
    ctx.arc(centerXTop, centerYTop, radiusBig, 0, 2 * Math.PI);
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(centerXTop, centerYTop, radiusSmall, 0, 2 * Math.PI);
    ctx.stroke();

    // Draw bottom circles
    const centerYBottom = y + circleCenterYBottom * scaleY;

    ctx.beginPath();
    ctx.arc(centerXTop, centerYBottom, radiusBig, 0, 2 * Math.PI);
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(centerXTop, centerYBottom, radiusSmall, 0, 2 * Math.PI);
    ctx.stroke();
  }

  // Function to draw the background pattern
  static drawBackgroundWhite(ctx, startPoint, width, height) {
    ctx.fillStyle = "white";
    ctx.fillRect(startPoint.x, startPoint.y, width, height);
  }

  // Function to draw the border pattern
  static drawBorder(ctx, startPoint, width, height) {
    ctx.lineWidth = 1;
    ctx.strokeStyle = "#4A90E2";
    ctx.strokeRect(startPoint.x, startPoint.y, width, height);
  }
}
